/**
 * Dashboard - Main bucket list dashboard (Refactored)
 * 
 * Components extracted:
 * - DashboardNav - Top navigation
 * - BucketItemCard - Individual item cards
 * - MobileBottomNav - Mobile navigation
 * - AddEditItemModal - Add/Edit item modal
 * - PastTripModal - Log past trips modal
 */

import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format } from "date-fns";
import { Plus, ArrowUpDown } from "lucide-react";

// Extracted components
import DashboardNav from "@/components/DashboardNav";
import BucketItemCard from "@/components/BucketItemCard";
import MobileBottomNav from "@/components/MobileBottomNav";
import AddEditItemModal from "@/components/AddEditItemModal";
import PastTripModal from "@/components/PastTripModal";
import ExplorerRank, { getRank } from "@/components/ExplorerRank";
import DashboardStats from "@/components/DashboardStats";
import ItemsFilter from "@/components/ItemsFilter";
import ItemsGrid from "@/components/ItemsGrid";

// Other modals/components
import ShareAchievementModal from "@/components/ShareAchievementModal";
import ItineraryPanel from "@/components/ItineraryPanel";
import PhotoGallery from "@/components/PhotoGallery";
import CommentsPanel from "@/components/CommentsPanel";
import TripSummary from "@/components/TripSummary";
import RemindersPanel from "@/components/RemindersPanel";
import { ExportModal } from "@/components/ExportButton";
import GroupsPanel from "@/components/GroupsPanel";
import GroupView from "@/components/GroupView";
import MemoriesTimeline from "@/components/MemoriesTimeline";
import Scrapbook from "@/components/Scrapbook";
import TripSuggestions from "@/components/TripSuggestions";
import WorldMap from "@/components/WorldMap";
import TravelSearch from "@/components/TravelSearch";
import BudgetTracker from "@/components/BudgetTracker";
import PackingList from "@/components/PackingList";
import SocialShare from "@/components/SocialShare";
import UpgradeModal from "@/components/UpgradeModal";
import Achievements from "@/components/Achievements";
import { MilestoneCelebration, ComebackMissions, StreakSaveCard, YearInReviewExport } from "@/components/premium";
import ExploreDestination from "@/components/ExploreDestination";
import useAnalytics, { AnalyticsEvents } from "@/hooks/useAnalytics";
import { OnboardingTour, useOnboardingTour } from "@/components/OnboardingTour";
import InterestsOnboarding from "@/components/InterestsOnboarding";
import useRealtime from "@/hooks/useRealtime";
import PullToRefresh from "@/components/PullToRefresh";
import OfflineBanner from "@/components/OfflineBanner";
import useOfflineSync from "@/hooks/useOfflineSync";
import ReferralPanel from "@/components/ReferralPanel";
import StreakSaveModal from "@/components/StreakSaveModal";
import StreakRestoreModal from "@/components/premium/StreakRestoreModal";
import NextAdventureCard from "@/components/premium/NextAdventureCard";
import WinbackBanner from "@/components/WinbackBanner";
import RenewalNudgeBanner from "@/components/RenewalNudgeBanner";

const INITIAL_FORM_DATA = {
  title: "",
  description: "",
  category: "experiences",
  priority: "medium",
  target_date: null,
  start_date: null,
  end_date: null,
  is_private: false,
  notes: "",
  location: null,
  country: null,
  country_code: null,
  latitude: null,
  longitude: null,
  group_id: null,
};

const INITIAL_PAST_TRIP_FORM = {
  title: "",
  description: "",
  country: "",
  country_code: "",
  location: "",
  category: "travel",
  visited_date: null,
  photos: [],
};

const Dashboard = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { trackEvent } = useAnalytics();
  
  // Offline sync
  const {
    isOnline,
    isSyncing,
    pendingCount,
    syncPendingChanges,
    cacheItems,
    getItems: getOfflineItems,
    queueCreateItem,
    queueUpdateItem,
    queueDeleteItem,
    queueCompleteItem,
  } = useOfflineSync(token);
  
  // Data state
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  
  // UI state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterCompleted, setFilterCompleted] = useState("all");
  const [sortOrder, setSortOrder] = useState("desc"); // desc = newest first
  
  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [shareItem, setShareItem] = useState(null);
  const [itineraryItem, setItineraryItem] = useState(null);
  const [photoItem, setPhotoItem] = useState(null);
  const [commentItem, setCommentItem] = useState(null);
  const [summaryItem, setSummaryItem] = useState(null);
  const [showReminders, setShowReminders] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showGroups, setShowGroups] = useState(false);
  const [showMemories, setShowMemories] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showWorldMap, setShowWorldMap] = useState(false);
  const [tripDataVersion, setTripDataVersion] = useState(0); // Increments on every trip mutation to trigger insights refresh
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showGroupView, setShowGroupView] = useState(false);
  const [showScrapbook, setShowScrapbook] = useState(false);
  const [showTravelSearch, setShowTravelSearch] = useState(false);
  const [travelSearchItem, setTravelSearchItem] = useState(null);
  const [showBudget, setShowBudget] = useState(false);
  const [budgetItem, setBudgetItem] = useState(null);
  const [showPacking, setShowPacking] = useState(false);
  const [packingItem, setPackingItem] = useState(null);
  const [showSocialShare, setShowSocialShare] = useState(false);
  const [socialShareItem, setSocialShareItem] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState("ai_suggestions");
  const [showPastTrips, setShowPastTrips] = useState(false);
  const [showAchievements, setShowAchievements] = useState(false);
  const [showExplore, setShowExplore] = useState(false);
  const [exploreCountry, setExploreCountry] = useState("");
  const [addToGroup, setAddToGroup] = useState(null);
  const [userRank, setUserRank] = useState(null); // Track user's travel rank
  const [unseenAchievements, setUnseenAchievements] = useState([]);
  
  // Growth features state
  const [showReferrals, setShowReferrals] = useState(false);
  const [showStreakSave, setShowStreakSave] = useState(false);
  const [showYearInReview, setShowYearInReview] = useState(false);
  const [streakStatus, setStreakStatus] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [pastTripForm, setPastTripForm] = useState(INITIAL_PAST_TRIP_FORM);
  const [savingPastTrip, setSavingPastTrip] = useState(false);

  // Deep-link: /dashboard?group=<id> opens the requested group's GroupView.
  // Used from the Profile page's "Open" button so users can actually access
  // their groups (previously had no entry point from Profile). Strips the
  // param after handling so a refresh doesn't re-trigger.
  useEffect(() => {
    if (!token) return;
    const params = new URLSearchParams(location.search);
    const groupId = params.get("group");
    if (!groupId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/groups/${groupId}`);
        if (cancelled) return;
        setSelectedGroup(res.data);
        setShowGroupView(true);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't open that group.");
      } finally {
        // Clear the query param so it doesn't fire again on next state change
        navigate("/dashboard", { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [token, location.search, navigate]);
  
  // Onboarding
  const { showTour, checkAndShowTour, closeTour } = useOnboardingTour();
  const [showInterests, setShowInterests] = useState(false);
  
  // Real-time updates
  const { connected, lastUpdate } = useRealtime(user?.id);
  
  // Check if user is admin
  const isAdmin = user?.email && ['admin@duolyst.com', 'countrypicker_test@test.com', 'ohaughton2@gmail.com'].includes(user.email);

  // Refresh user data on mount and when coming back to the page (for partner updates)
  useEffect(() => {
    if (user?.id && refreshUser) {
      refreshUser();
    }
  }, []); // Run once on mount

  // Fetch items
  const fetchItems = useCallback(async () => {
    try {
      // If offline, load from cache
      if (!isOnline) {
        const cachedItems = await getOfflineItems();
        if (cachedItems && cachedItems.length > 0) {
          setItems(cachedItems);
          return;
        }
      }
      
      let url = `${API}/items`;
      const params = [];
      if (filterCategory !== "all") params.push(`category=${filterCategory}`);
      if (filterCompleted !== "all") params.push(`completed=${filterCompleted === "completed"}`);
      if (params.length) url += `?${params.join("&")}`;
      
      const response = await axios.get(url);
      setItems(response.data);
      
      // Cache items for offline use
      if (response.data && response.data.length > 0) {
        cacheItems(response.data);
      }
    } catch (error) {
      // If network error, try loading from cache
      if (!isOnline || error.code === 'ERR_NETWORK') {
        const cachedItems = await getOfflineItems();
        if (cachedItems && cachedItems.length > 0) {
          setItems(cachedItems);
          return;
        }
      }
      toast.error("Failed to load items");
    }
  }, [filterCategory, filterCompleted, isOnline, getOfflineItems, cacheItems]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats");
    }
  }, []);

  // Fetch user rank for dynamic styling
  const fetchUserRank = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/map/visited`);
      const countriesVisited = response.data?.stats?.countries_visited || 0;
      const percentage = (countriesVisited / 195) * 100;
      const rank = getRank(percentage);
      setUserRank(rank);
    } catch (error) {
      // Default to first rank if can't fetch
      setUserRank(getRank(0));
    }
  }, []);

  // Fetch subscription
  const fetchSubscription = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscription/current`);
      setSubscription(response.data.subscription);
    } catch (error) {
      console.error("Failed to fetch subscription");
    }
  }, []);

  // Pull to refresh handler
  const handleRefresh = useCallback(async () => {
    await Promise.all([fetchItems(), fetchStats(), refreshUser()]);
    toast.success("Refreshed!");
  }, [fetchItems, fetchStats, refreshUser]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchItems(), fetchStats(), fetchSubscription(), fetchUserRank()]);
      setLoading(false);

      // Phase 5 — warm the AI suggestion pool during idle time so the
      // next "Get suggestions" tap is near-instant. Best-effort only —
      // a 403 (free tier) or transient failure must never block the
      // dashboard. Uses requestIdleCallback where available so we never
      // compete with the dashboard's first paint.
      const warmAiPool = () => {
        axios.post(`${API}/suggestions/trips/warm`).catch(() => {});
      };
      if (typeof window !== "undefined") {
        if ("requestIdleCallback" in window) {
          window.requestIdleCallback(warmAiPool, { timeout: 4000 });
        } else {
          setTimeout(warmAiPool, 1500);
        }
      }

      // Check for onboarding - use user-specific key AND check backend preferences
      if (user) {
        const userKey = `duolyst_interests_seen_${user.id}`;
        const hasSeenInterests = localStorage.getItem(userKey);
        
        // Also check if user has preferences saved on backend
        try {
          const prefResponse = await axios.get(`${API}/user/preferences`);
          const hasPreferences = prefResponse.data?.preferences && 
            Object.keys(prefResponse.data.preferences).length > 0 &&
            (prefResponse.data.preferences.travel_style?.length > 0 || 
             prefResponse.data.preferences.activities?.length > 0);
          
          // Show onboarding only if: hasn't seen it AND has no saved preferences
          if (!hasSeenInterests && !hasPreferences) {
            setShowInterests(true);
          }
        } catch (error) {
          // If can't fetch preferences, fall back to localStorage only
          if (!hasSeenInterests) {
            setShowInterests(true);
          }
        }
      }
    };
    loadData();
  }, [fetchItems, fetchStats, fetchSubscription, user]);

  // One-time celebration: surface badges awarded retroactively while the user
  // wasn't online (e.g. server-side backfills). Pops a single modal listing
  // them, then marks them all seen so it doesn't show again.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/achievements/unseen`);
        if (cancelled) return;
        const list = res.data?.unseen || [];
        if (list.length > 0) setUnseenAchievements(list);
      } catch (e) {
        // silent — non-critical
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const dismissUnseenAchievements = async () => {
    setUnseenAchievements([]);
    try {
      await axios.post(`${API}/achievements/mark-seen`);
    } catch (e) {
      // silent — they'll re-appear next load if this fails, which is acceptable
    }
  };

  // Re-fetch when filters change
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Real-time update handler
  useEffect(() => {
    if (lastUpdate) {
      fetchItems();
      fetchStats();
    }
  }, [lastUpdate, fetchItems, fetchStats]);

  // Reset form
  const resetForm = () => {
    setEditItem(null);
    setFormData(INITIAL_FORM_DATA);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    const payload = {
      ...formData,
      // target_date = the canonical travel date for analytics.
      // Priority: start_date (if set) > existing target_date > null
      target_date: formData.start_date 
        ? format(formData.start_date, "yyyy-MM-dd") 
        : formData.target_date 
          ? format(formData.target_date, "yyyy-MM-dd") 
          : null,
      start_date: formData.start_date ? format(formData.start_date, "yyyy-MM-dd") : null,
      end_date: formData.end_date ? format(formData.end_date, "yyyy-MM-dd") : null,
      group_id: addToGroup?.id || formData.group_id || null,
    };

    try {
      if (editItem) {
        // Handle offline update
        if (!isOnline) {
          await queueUpdateItem(editItem.id, payload);
          setItems(prev => prev.map(item => 
            item.id === editItem.id ? { ...item, ...payload, _pendingSync: true } : item
          ));
          toast.success("Item updated (will sync when online)");
        } else {
          await axios.put(`${API}/items/${editItem.id}`, payload);
          toast.success("Item updated!");
        }
      } else {
        // Handle offline create
        if (!isOnline) {
          const tempId = `temp_${Date.now()}`;
          const localItem = await queueCreateItem(payload, tempId);
          setItems(prev => [localItem, ...prev]);
          if (addToGroup) {
            toast.success(`Item added to ${addToGroup.name} (will sync when online)!`);
          } else {
            toast.success("Item added (will sync when online)!");
          }
        } else {
          const res = await axios.post(`${API}/items`, payload);
          if (addToGroup) {
            toast.success(`Item added to ${addToGroup.name}!`);
          } else {
            toast.success("Item added to your bucket list!");
          }
          // Surface any newly-earned planning-stage badges (Dreamer, Wanderlust, etc.)
          if (res.data?.new_achievements?.length > 0) {
            res.data.new_achievements.forEach((achievementId, index) => {
              setTimeout(() => {
                toast.success(
                  `Achievement Unlocked: ${achievementId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`,
                  {
                    icon: "🏆",
                    duration: 5000,
                    action: {
                      label: "View",
                      onClick: () => setShowAchievements(true),
                    },
                  }
                );
              }, (index + 1) * 1500);
            });
          }
        }
        trackEvent(AnalyticsEvents.ITEM_CREATED, { category: formData.category });
      }
      
      setShowAddModal(false);
      setAddToGroup(null);
      resetForm();
      if (isOnline) {
        fetchItems();
        fetchStats();
      }
      setTripDataVersion(v => v + 1); // Trigger insights refresh
    } catch (error) {
      toast.error("Failed to save item");
    }
  };

  // Handle saving past trip
  const handleSavePastTrip = async (e) => {
    e.preventDefault();
    if (!pastTripForm.title.trim()) {
      toast.error("Please enter a title for your trip");
      return;
    }
    if (!pastTripForm.country_code) {
      toast.error("Please select a country to add it to your map");
      return;
    }
    if (!pastTripForm.visited_date) {
      toast.error("Please enter when you visited");
      return;
    }

    setSavingPastTrip(true);
    try {
      const payload = {
        title: pastTripForm.title,
        description: pastTripForm.description || `Visited ${pastTripForm.country}`,
        category: pastTripForm.category,
        priority: "medium",
        location: pastTripForm.location || pastTripForm.country,
        country: pastTripForm.country,
        country_code: pastTripForm.country_code,
        target_date: format(pastTripForm.visited_date, "yyyy-MM-dd"),
        is_past_trip: true,
      };

      const res = await axios.post(`${API}/items/past-trip`, payload);
      
      toast.success(
        <div>
          <p className="font-semibold">Past trip logged! 🌍</p>
          <p className="text-sm">{pastTripForm.country} added to your map</p>
        </div>,
        { duration: 4000 }
      );

      if (res.data.new_achievements?.length > 0) {
        res.data.new_achievements.forEach((badge, index) => {
          setTimeout(() => {
            toast.success(`Achievement Unlocked: ${badge.name || badge.replace(/_/g, ' ')}`, {
              icon: "🏆",
              duration: 5000,
            });
          }, (index + 1) * 1500);
        });
      }

      setPastTripForm(INITIAL_PAST_TRIP_FORM);
      setShowPastTrips(false);
      fetchItems();
      fetchStats();
      setTripDataVersion(v => v + 1); // Trigger insights refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save past trip");
    } finally {
      setSavingPastTrip(false);
    }
  };

  // Handle toggle complete
  const handleToggleComplete = async (item) => {
    try {
      // Handle offline complete
      if (!isOnline) {
        const newCompletedState = !item.completed;
        await queueCompleteItem(item.id, { completed: newCompletedState });
        setItems(prev => prev.map(i => 
          i.id === item.id ? { ...i, completed: newCompletedState, _pendingSync: true } : i
        ));
        toast.success(newCompletedState ? "Completed! (will sync when online) 🎉" : "Marked as pending (will sync when online)");
        return;
      }
      
      const res = await axios.post(`${API}/items/${item.id}/toggle-complete`);
      if (!item.completed) {
        toast.success("Completed! Great job! 🎉", {
          action: {
            label: "Share",
            onClick: () => setShareItem(res.data),
          },
        });
        
        if (res.data.new_achievements?.length > 0) {
          res.data.new_achievements.forEach((achievementId, index) => {
            setTimeout(() => {
              toast.success(`Achievement Unlocked: ${achievementId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`, {
                icon: "🏆",
                duration: 5000,
                action: {
                  label: "View",
                  onClick: () => setShowAchievements(true),
                },
              });
            }, (index + 1) * 1500);
          });
        }
        
        const wasFirstCompletion = stats && stats.completed_items === 0;
        checkAndShowTour(wasFirstCompletion);
      } else {
        toast.success("Marked as pending");
      }
      fetchItems();
      fetchStats();
      // Refresh user data to update ranking immediately
      refreshUser();
      setTripDataVersion(v => v + 1); // Trigger insights refresh
    } catch (error) {
      toast.error("Failed to update item");
    }
  };

  // Handle delete
  const handleDelete = async (item) => {
    try {
      // Handle offline delete
      if (!isOnline) {
        await queueDeleteItem(item.id);
        setItems(prev => prev.filter(i => i.id !== item.id));
        toast.success("Item removed (will sync when online)");
        return;
      }
      
      await axios.delete(`${API}/items/${item.id}`);
      toast.success("Item removed");
      fetchItems();
      fetchStats();
      setTripDataVersion(v => v + 1); // Trigger insights refresh
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  // Handle hiding items from former partners
  const handleHide = async (item) => {
    try {
      await axios.post(`${API}/items/${item.id}/hide`);
      setItems(prev => prev.filter(i => i.id !== item.id));
      toast.success("Item hidden from your list");
      fetchStats();
      setTripDataVersion(v => v + 1);
    } catch (error) {
      toast.error("Failed to hide item");
    }
  };

  // Handle edit
  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      title: item.title,
      description: item.description || "",
      category: item.category,
      priority: item.priority,
      target_date: item.target_date ? new Date(item.target_date) : null,
      start_date: item.start_date ? new Date(item.start_date) : (item.target_date ? new Date(item.target_date) : null),
      end_date: item.end_date ? new Date(item.end_date) : null,
      is_private: item.is_private,
      notes: item.notes || "",
      location: item.location || null,
      country: item.country || null,
      country_code: item.country_code || null,
      latitude: item.latitude || null,
      longitude: item.longitude || null,
    });
    setShowAddModal(true);
  };

  // Handle notification click
  const handleNotificationClick = async (itemId) => {
    try {
      const response = await axios.get(`${API}/items/${itemId}`);
      setCommentItem(response.data);
    } catch (error) {
      toast.error("Could not open item - it may have been deleted");
    }
  };

  // Handle interests onboarding
  const handleInterestsComplete = async (selections) => {
    setShowInterests(false);
    // Save user-specific localStorage key
    if (user) {
      localStorage.setItem(`duolyst_interests_seen_${user.id}`, "true");
    }
    // Save preferences to backend
    try {
      await axios.put(`${API}/user/preferences`, selections);
    } catch (error) {
      console.error("Failed to save preferences:", error);
    }
    fetchItems();
  };

  const handleInterestsSkip = () => {
    setShowInterests(false);
    // Save user-specific localStorage key
    if (user) {
      localStorage.setItem(`duolyst_interests_seen_${user.id}`, "true");
    }
  };

  // Filter and sort items
  const filteredItems = items.filter(item => 
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((a, b) => {
    const dateA = new Date(a.target_date || a.created_at);
    const dateB = new Date(b.target_date || b.created_at);
    return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary text-xl font-serif">Loading your dreams...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16 md:pb-8">
      {/* Growth Banners - Win-back and Renewal Nudges */}
      <WinbackBanner />
      <RenewalNudgeBanner />
      
      {/* Offline Banner */}
      <OfflineBanner 
        isOnline={isOnline}
        isSyncing={isSyncing}
        pendingCount={pendingCount}
        onSync={syncPendingChanges}
      />
      
      {/* Navigation */}
      <DashboardNav
        user={user}
        subscription={subscription}
        connected={connected}
        isAdmin={isAdmin}
        onShowWorldMap={() => setShowWorldMap(true)}
        onShowTravelSearch={() => setShowTravelSearch(true)}
        onShowExplore={() => { setExploreCountry(""); setShowExplore(true); }}
        onShowBudget={() => { setBudgetItem(null); setShowBudget(true); }}
        onShowScrapbook={() => setShowScrapbook(true)}
        onShowSuggestions={() => setShowSuggestions(true)}
        onShowMemories={() => setShowMemories(true)}
        onShowGroups={() => setShowGroups(true)}
        onShowPastTrips={() => setShowPastTrips(true)}
        onShowAchievements={() => setShowAchievements(true)}
        onShowReminders={() => setShowReminders(true)}
        onShowExport={() => setShowExport(true)}
        onShowYearInReview={() => setShowYearInReview(true)}
        onNotificationClick={handleNotificationClick}
      />

      <PullToRefresh onRefresh={handleRefresh} className="flex-1">
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Next Adventure — signature countdown card, first thing users see */}
        <NextAdventureCard
          refreshKey={tripDataVersion}
          onPlanTrip={() => {
            setEditItem(null);
            resetForm();
            setShowAddModal(true);
          }}
        />

        {/* Email Verification Banner */}
        {user && !user.email_verified && (
          <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="flex-1">
                <h3 className="font-medium text-amber-800 dark:text-amber-200">Verify your email</h3>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Please verify your email address to unlock all features
                </p>
              </div>
              <Button
                onClick={() => navigate('/verify-email')}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg shrink-0"
                data-testid="verify-email-banner-btn"
              >
                Verify Now
              </Button>
            </div>
          </div>
        )}

        {/* Stats & Explorer Rank Section */}
        <DashboardStats stats={stats} onOpenMap={() => setShowWorldMap(true)} />

        {/* Filters & Search */}
        <ItemsFilter
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filterCategory={filterCategory}
          onCategoryChange={setFilterCategory}
          filterCompleted={filterCompleted}
          onCompletedChange={setFilterCompleted}
          sortOrder={sortOrder}
          onSortOrderChange={setSortOrder}
        />

        {/* Items Grid */}
        <ItemsGrid
          items={filteredItems}
          searchQuery={searchQuery}
          currentUserId={user.id}
          partnerId={user.partner_id}
          onToggleComplete={handleToggleComplete}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onHide={handleHide}
          onShare={setShareItem}
          onSummary={setSummaryItem}
          onItinerary={setItineraryItem}
          onTravelSearch={(item) => { setTravelSearchItem(item); setShowTravelSearch(true); }}
          onBudget={(item) => { setBudgetItem(item); setShowBudget(true); }}
          onPhotos={setPhotoItem}
          onComments={setCommentItem}
          onPacking={(item) => { setPackingItem(item); setShowPacking(true); }}
          onSocialShare={(item) => { setSocialShareItem(item); setShowSocialShare(true); }}
          onAddItem={() => setShowAddModal(true)}
          rankColor={userRank?.color || "from-green-400 to-green-600"}
          rankSolidColor={userRank?.solidColor || "bg-green-500"}
          rankBorderColor={userRank?.borderColor || "border-green-500"}
        />

        {/* Items list moved above - floating button moved outside PullToRefresh */}
      </main>
      </PullToRefresh>

      {/* Floating Add Button - MUST be outside PullToRefresh to avoid overflow:hidden clipping.
          z-30 sits ABOVE normal page content but BELOW any open shadcn Dialog (z-50). When a
          modal is open we also hide the button so it can never visually overlap interactive
          elements inside the dialog (e.g. the Public Streak toggle). */}
      {!showWorldMap &&
        !showAchievements &&
        !showAddModal &&
        !showMemories &&
        !showReminders &&
        !showExport &&
        !showReferrals &&
        !showExplore &&
        !showUpgrade &&
        !showStreakSave &&
        !showYearInReview &&
        unseenAchievements.length === 0 && (
        <Button
          onClick={() => { setEditItem(null); resetForm(); setShowAddModal(true); }}
          className="fixed bottom-24 sm:bottom-8 right-6 w-14 h-14 rounded-full bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/30 btn-press z-30"
          data-testid="add-item-fab"
        >
          <Plus className="w-6 h-6" />
        </Button>
      )}

      {/* ===== MODALS ===== */}
      
      {/* Add/Edit Item Modal */}
      <AddEditItemModal
        isOpen={showAddModal}
        onClose={(open) => {
          setShowAddModal(open);
          if (!open) setAddToGroup(null);
        }}
        editItem={editItem}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        onReset={resetForm}
        addToGroup={addToGroup}
      />

      {/* Past Trip Modal */}
      <PastTripModal
        isOpen={showPastTrips}
        onClose={setShowPastTrips}
        formData={pastTripForm}
        setFormData={setPastTripForm}
        onSubmit={handleSavePastTrip}
        saving={savingPastTrip}
      />

      {/* Share Achievement Modal */}
      <ShareAchievementModal
        item={shareItem}
        userName={user.name}
        partnerName={user.partner_name}
        open={!!shareItem}
        onClose={() => setShareItem(null)}
        foundingExplorer={user.founding_explorer}
        foundingExplorerNumber={user.founding_explorer_number}
      />

      {/* Itinerary Panel */}
      <ItineraryPanel
        bucketItemId={itineraryItem?.id}
        bucketItemTitle={itineraryItem?.title}
        isOpen={!!itineraryItem}
        onClose={() => setItineraryItem(null)}
      />

      {/* Photo Gallery */}
      <PhotoGallery
        bucketItemId={photoItem?.id}
        bucketItemTitle={photoItem?.title}
        isOpen={!!photoItem}
        onClose={() => setPhotoItem(null)}
        isGroupItem={!!photoItem?.group_id}
      />

      {/* Comments Panel */}
      <CommentsPanel
        bucketItemId={commentItem?.id}
        bucketItemTitle={commentItem?.title}
        isOpen={!!commentItem}
        onClose={() => setCommentItem(null)}
      />

      {/* Trip Summary */}
      <TripSummary
        bucketItemId={summaryItem?.id}
        isOpen={!!summaryItem}
        onClose={() => setSummaryItem(null)}
      />

      {/* Reminders Panel */}
      <RemindersPanel
        isOpen={showReminders}
        onClose={() => setShowReminders(false)}
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={showExport}
        onClose={() => setShowExport(false)}
      />

      {/* Groups Panel */}
      <GroupsPanel
        isOpen={showGroups}
        onClose={() => setShowGroups(false)}
        onSelectGroup={(group) => {
          setSelectedGroup(group);
          setShowGroups(false);
          setShowGroupView(true);
        }}
      />

      {/* Group View */}
      <GroupView
        group={selectedGroup}
        isOpen={showGroupView}
        onClose={() => {
          setShowGroupView(false);
          setSelectedGroup(null);
        }}
        onAddItem={(group) => {
          setAddToGroup(group);
          setShowAddModal(true);
        }}
      />

      {/* Memories Timeline */}
      <MemoriesTimeline
        isOpen={showMemories}
        onClose={() => setShowMemories(false)}
      />

      {/* Scrapbook */}
      <Scrapbook
        isOpen={showScrapbook}
        onClose={() => setShowScrapbook(false)}
      />

      {/* Trip Suggestions */}
      <TripSuggestions
        isOpen={showSuggestions}
        onClose={() => setShowSuggestions(false)}
        onItemAdded={() => {
          fetchItems();
          fetchStats();
        }}
      />

      {/* World Map */}
      <WorldMap
        isOpen={showWorldMap}
        onClose={() => setShowWorldMap(false)}
        user={user}
        dataVersion={tripDataVersion}
        onAddItem={(countryData) => {
          // Validate country data
          if (!countryData.country_code) {
            toast.error("Unable to add this location - missing country code");
            return;
          }
          
          if (countryData.isPastTrip) {
            setPastTripForm(prev => ({
              ...prev,
              title: `Trip to ${countryData.country}`,
              country: countryData.country,
              country_code: countryData.country_code,
              location: countryData.country,
            }));
            setShowPastTrips(true);
          } else {
            setFormData(prev => ({
              ...prev,
              title: `Visit ${countryData.country}`,
              country: countryData.country,
              country_code: countryData.country_code,
              location: countryData.country,
              category: "travel",
            }));
            setEditItem(null);
            setShowAddModal(true);
          }
        }}
      />

      {/* Travel Search */}
      <TravelSearch
        isOpen={showTravelSearch}
        onClose={() => {
          setShowTravelSearch(false);
          setTravelSearchItem(null);
        }}
        bucketItem={travelSearchItem}
      />

      {/* Budget Tracker */}
      <BudgetTracker
        isOpen={showBudget}
        onClose={() => {
          setShowBudget(false);
          setBudgetItem(null);
        }}
        bucketItem={budgetItem}
      />

      {/* Packing List */}
      <Dialog open={showPacking} onOpenChange={(open) => { setShowPacking(open); if (!open) setPackingItem(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto bg-card border-none rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-foreground flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                📦
              </span>
              Packing List
            </DialogTitle>
            {packingItem && (
              <p className="text-sm text-muted-foreground">{packingItem.title}</p>
            )}
          </DialogHeader>
          {packingItem && (
            <PackingList 
              itemId={packingItem.id} 
              token={axios.defaults.headers.common["Authorization"]?.replace("Bearer ", "")} 
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Social Share Modal */}
      <SocialShare
        item={socialShareItem}
        isOpen={showSocialShare}
        onClose={() => { setShowSocialShare(false); setSocialShareItem(null); }}
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        feature={upgradeFeature}
      />

      {/* Explore Destinations Modal */}
      <ExploreDestination
        isOpen={showExplore}
        onClose={() => setShowExplore(false)}
        initialCountry={exploreCountry}
        onAddToBucketList={(data) => {
          setFormData({
            ...formData,
            title: data.title,
            description: data.description,
            location: data.location,
            country: data.country,
            category: data.category,
            estimated_cost: data.estimated_cost,
            currency: data.currency || "USD",
          });
          setShowExplore(false);
          setEditItem(null);
          setShowAddModal(true);
        }}
      />

      {/* Achievements Modal */}
      <Achievements
        isOpen={showAchievements}
        onClose={() => setShowAchievements(false)}
      />

      {/* Onboarding Tour */}
      <OnboardingTour
        isOpen={showTour}
        onClose={closeTour}
        onOpenWorldMap={() => {
          closeTour();
          setShowWorldMap(true);
        }}
        stats={stats}
      />

      {/* Interests Onboarding */}
      <InterestsOnboarding
        isOpen={showInterests}
        onComplete={handleInterestsComplete}
        onSkip={handleInterestsSkip}
      />

      {/* Referrals Panel Modal */}
      <Dialog open={showReferrals} onOpenChange={setShowReferrals}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="referral-panel-description">
          <DialogHeader className="sr-only">
            <DialogTitle>Invite Friends & Earn Rewards</DialogTitle>
          </DialogHeader>
          <span id="referral-panel-description" className="sr-only">
            Share your referral code and earn free premium features
          </span>
          <ReferralPanel onClose={() => setShowReferrals(false)} />
        </DialogContent>
      </Dialog>

      {/* Streak Save Modal */}
      <StreakSaveModal
        open={showStreakSave}
        onOpenChange={setShowStreakSave}
      />

      {/* Reactive Streak Restore — pops automatically when streak just broke + saves > 0 */}
      <StreakRestoreModal />

      {/* Year in Review MP4 Export */}
      <YearInReviewExport
        isOpen={showYearInReview}
        onClose={() => setShowYearInReview(false)}
      />

      {/* Milestone Celebration (Confetti + Share) */}
      <MilestoneCelebration />

      {/* Mobile Navigation */}
      <MobileBottomNav
        user={user}
        onShowWorldMap={() => setShowWorldMap(true)}
        onShowSuggestions={() => setShowSuggestions(true)}
        onShowGroups={() => setShowGroups(true)}
        onShowBudget={() => { setBudgetItem(null); setShowBudget(true); }}
        onShowPastTrips={() => setShowPastTrips(true)}
        onShowAchievements={() => setShowAchievements(true)}
        onShowTravelSearch={() => setShowTravelSearch(true)}
        onShowExplore={() => { setExploreCountry(""); setShowExplore(true); }}
        onShowScrapbook={() => setShowScrapbook(true)}
        onShowMemories={() => setShowMemories(true)}
        onShowReminders={() => setShowReminders(true)}
        onShowExport={() => setShowExport(true)}
        onShowReferrals={() => setShowReferrals(true)}
        onShowYearInReview={() => setShowYearInReview(true)}
      />

      {/* One-time celebration modal for badges awarded while user was offline */}
      <Dialog
        open={unseenAchievements.length > 0}
        onOpenChange={(open) => { if (!open) dismissUnseenAchievements(); }}
      >
        <DialogContent
          className="max-w-md"
          data-testid="unseen-achievements-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif text-center">
              🏆 You've unlocked {unseenAchievements.length} {unseenAchievements.length === 1 ? "badge" : "badges"}!
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground text-center mb-2">
            These were earned since your last visit. Tap to view them all.
          </p>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {unseenAchievements.map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-primary/10 border border-primary/20"
                data-testid={`unseen-achievement-${a.id}`}
              >
                <div className="text-2xl">🏆</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{a.title}</div>
                  <div className="text-xs text-muted-foreground truncate">{a.description}</div>
                </div>
                <div className="text-xs uppercase tracking-wide text-primary font-semibold">
                  {a.tier}
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              className="flex-1 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted"
              onClick={dismissUnseenAchievements}
              data-testid="unseen-achievements-dismiss-btn"
            >
              Got it
            </button>
            <button
              type="button"
              className="flex-1 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
              onClick={() => { dismissUnseenAchievements(); setShowAchievements(true); }}
              data-testid="unseen-achievements-view-btn"
            >
              View all badges
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
